/**
 * The Bespoke theme-document type + the client-side applier.
 *
 * `applyTokens` mirrors the PHP BespokeCompiler::emitCssVariables() exactly, but
 * runs in the browser so the editor can repaint with ZERO round-trip: every
 * control just calls applyTokens() (or setProperty for a single value) and the
 * page updates instantly. This is the whole point of the CSS-variable layer.
 */

/**
 * Which colour variant to read. The base axis (light/dark) is driven by
 * <html data-theme>; the high-contrast variants are applied automatically for
 * visitors whose OS requests more contrast (the compiler emits them inside an
 * `@media (prefers-contrast: more)` block) and are previewable in the editor.
 */

/** True for the dark side of the palette (dark or darkHC). */
export function isDarkMode(mode) {
  return mode === 'dark' || mode === 'darkHC';
}

/**
 * Resolve a colour for the given mode, falling back to the base light/dark value
 * when a high-contrast variant hasn't been set yet (documents saved before HC
 * existed). Keeps the editor swatches and live preview from going blank.
 */
export function colorAt(pair, mode) {
  return pair && (pair[mode] || pair[isDarkMode(mode) ? 'dark' : 'light']) || '#000';
}
const ELEVATION = ['none', '0 1px 3px rgba(20,22,50,.08)', '0 8px 30px rgba(20,22,50,.08)', '0 18px 50px rgba(20,22,50,.16)'];

/** Live-apply the admin's custom CSS (editor preview) via a single <style>. */
export function applyCustomCss(css) {
  let el = document.getElementById('bespoke-custom-live');
  if (!el) {
    el = document.createElement('style');
    el.id = 'bespoke-custom-live';
    document.head.appendChild(el);
  }
  el.textContent = (css || '').replace(/<\/?(style|script)/gi, '');
}

/** Apply a full token set to :root. Optionally scope to a single var for speed. */
export function applyTokens(tokens, mode = 'light') {
  const r = document.documentElement.style;
  for (const [role, pair] of Object.entries(tokens.color)) {
    r.setProperty(`--bespoke-color-${role}`, colorAt(pair, mode));
  }
  const t = tokens.type;
  r.setProperty('--bespoke-font-body', `'${t.bodyFamily}'`);
  r.setProperty('--bespoke-font-heading', `'${t.headingFamily}'`);
  r.setProperty('--bespoke-base-size', `${t.baseSize}px`);
  r.setProperty('--bespoke-scale', `${t.scaleRatio}`);
  r.setProperty('--bespoke-line', `${t.lineHeight}`);
  r.setProperty('--bespoke-heading-weight', `${t.headingWeight}`);
  r.setProperty('--bespoke-radius', `${tokens.shape.radius}px`);
  r.setProperty('--bespoke-border-width', `${tokens.shape.borderWidth}px`);
  r.setProperty('--bespoke-elevation', ELEVATION[tokens.shape.elevation] ?? 'none');
  r.setProperty('--bespoke-density', `${tokens.space.density}`);
  r.setProperty('--bespoke-container', `${tokens.space.container}px`);
  r.setProperty('--bespoke-gutter', `${tokens.space.gutter}px`);
  r.setProperty('--bespoke-hero-height', tokens.layout.heroHeight > 0 ? `${tokens.layout.heroHeight}px` : 'auto');
  r.setProperty('--bespoke-page-hero-pad', `${tokens.layout.pageHeroPadding ?? 8}px`);
  r.setProperty('--bespoke-hero-image', tokens.layout.heroImage ? `url('${tokens.layout.heroImage.replace(/['\n\r]/g, '')}')` : 'none');
  r.setProperty('--bespoke-bg-image', tokens.layout.bgImage ? `url('${tokens.layout.bgImage.replace(/['\n\r]/g, '')}')` : 'none');
  if (tokens.layout.gridColumns === 'auto') {
    document.documentElement.setAttribute('data-bespoke-cols', 'auto');
  } else {
    document.documentElement.removeAttribute('data-bespoke-cols');
    r.setProperty('--bespoke-cols', `${tokens.layout.gridColumns}`);
  }

  // structural switches the stylesheet reads off the document root <html>
  // (same element the server renders them on via Document::extraAttributes)
  const b = document.documentElement;
  b.setAttribute('data-bespoke-side', tokens.layout.sidebar);
  b.setAttribute('data-bespoke-btn', tokens.shape.buttonStyle);
  b.setAttribute('data-bespoke-posts', tokens.shape.postStyle);
  b.setAttribute('data-bespoke-avatars', tokens.shape.avatarStyle);
  b.setAttribute('data-bespoke-fullwidth', tokens.layout.fullWidth ? 'on' : 'off');
  b.setAttribute('data-bespoke-header', tokens.layout.headerStyle);
  b.setAttribute('data-bespoke-header-center', tokens.layout.headerCentered ? 'on' : 'off');
  b.setAttribute('data-bespoke-sticky', tokens.layout.stickyHeader ? 'on' : 'off');
  b.setAttribute('data-bespoke-glass', tokens.layout.glass ? 'on' : 'off');
  b.setAttribute('data-bespoke-anim', tokens.layout.animations ? 'on' : 'off');
  b.setAttribute('data-bespoke-borders', tokens.layout.cardBorders);
  b.setAttribute('data-bespoke-hero', tokens.layout.hero ? 'on' : 'off');
  b.setAttribute('data-bespoke-hero-sized', tokens.layout.heroHeight > 0 ? 'on' : 'off');
  b.setAttribute('data-bespoke-hero-full', tokens.layout.heroFull ? 'on' : 'off');
  b.setAttribute('data-bespoke-hero-dismiss', tokens.layout.heroDismissible ? 'on' : 'off');
  // Non-dismissible → clear Flarum's "welcomeHidden" flag so the hero always shows.
  if (!tokens.layout.heroDismissible) {
    try {
      localStorage.removeItem('welcomeHidden');
    } catch (e) {}
  }
  b.setAttribute('data-bespoke-discussions', tokens.layout.discussionList);
}

/** Single-property live update — what sliders/pickers call on every input event. */
export function setToken(cssVar, value) {
  document.documentElement.style.setProperty(cssVar, value);
}
flarum.reg.add('ernestdefoe-bespoke', 'common/tokens', { isDarkMode: isDarkMode,colorAt: colorAt,applyCustomCss: applyCustomCss,applyTokens: applyTokens,setToken: setToken, });